<script setup lang="ts">
import type { ReconciliationRow, ResidualTarget } from '../internal/types.ts'
import {
  type Locale,
  type Money,
  formatMoney,
  isZeroMoney,
  negateMoney,
  requireCurrencyCode,
  subtractMoney,
} from '@taler/domain'
import { AccountIcon, ActionButton, StatusTag } from '@taler/ui'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import type { AccountLookup } from '../internal/account-index.ts'

const props = defineProps<{
  row: ReconciliationRow
  account: AccountLookup
  /** LIABILITY accounts are entered and shown as the positive amount owed. */
  isLiability: boolean
  saving: boolean
  changed: boolean
  errorDetail: string | undefined
  disabled: boolean
  locale: Locale
}>()

const emit = defineEmits<{ save: [accountId: string, observedBalance: Money]; clear: [accountId: string] }>()

const { t } = useI18n()

function toDisplay(value: Money): Money {
  return props.isLiability ? negateMoney(value) : value
}

function fromDisplay(value: Money): Money {
  return props.isLiability ? negateMoney(value) : value
}

// Pre-filled with the computed figure, per the captain's ruling - never a blank input.
const initial = props.row.observedBalance ?? props.row.computedBalance
const draft = ref<Money | null>(toDisplay(initial))
let timer: ReturnType<typeof setTimeout> | undefined
let edited = false

function schedule(): void {
  edited = true
  if (timer !== undefined) clearTimeout(timer)
  timer = setTimeout(commit, 600)
}

function commit(): void {
  if (timer !== undefined) clearTimeout(timer)
  timer = undefined
  if (!edited) return
  edited = false
  if (draft.value === null) emit('clear', props.row.accountId)
  else emit('save', props.row.accountId, fromDisplay(draft.value))
}

function confirm(): void {
  if (draft.value === null || props.saving) return
  edited = true
  commit()
}

const currency = computed(() => requireCurrencyCode(props.row.computedBalance.currency))
const computedDisplay = computed(() => toDisplay(props.row.computedBalance))
const observedDisplay = computed(() =>
  props.row.observedBalance === undefined ? undefined : toDisplay(props.row.observedBalance),
)
const difference = computed(() => {
  if (observedDisplay.value === undefined) return undefined
  return subtractMoney(observedDisplay.value, computedDisplay.value)
})
const matches = computed(() => difference.value !== undefined && isZeroMoney(difference.value))

function moneyText(value: Money, sign?: boolean): string {
  return formatMoney(value, {
    locale: props.locale,
    ...(sign === undefined ? {} : { sign }),
  })
}

const kindLabel = computed<string>(() => {
  const target: ResidualTarget = props.row.residualTarget
  return target === 'PNL' ? t('reconciliation.editor.gainLoss') : t('reconciliation.editor.correction')
})

const inputAriaLabel = computed(() =>
  t('reconciliation.editor.actualBalanceFor', { name: props.account.label }),
)
</script>

<template>
  <tr class="border-b border-[color:var(--color-border)]" :class="{ 'opacity-60': disabled }">
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
      <div class="flex items-center gap-[var(--space-inline)]">
        <span
          class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)]"
          :style="{ backgroundColor: `${account.color}22`, color: account.color }"
        >
          <AccountIcon :icon="account.icon" :label="account.label" />
        </span>
        <div>
          <p class="text-[color:var(--color-text)]">{{ account.label }}</p>
          <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
            {{ currency }} · {{ account.code }}
          </p>
        </div>
      </div>
    </td>
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] text-right align-middle tabular-nums">
      {{ moneyText(computedDisplay) }}
    </td>
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
      <AmountInput
        v-model="draft"
        :currency="currency"
        :locale="locale"
        allow-negative
        :show-total="false"
        :disabled="disabled"
        :aria-label="inputAriaLabel"
        class="w-40"
        @update:model-value="schedule"
        @blur="commit"
        @keydown.enter="confirm"
      />
    </td>
    <td class="h-[var(--density-row-tall)] px-[var(--space-cell)] align-middle">
      <StatusTag v-if="matches" tone="ok">{{ t('reconciliation.editor.matches') }}</StatusTag>
      <div v-else-if="difference !== undefined" class="flex items-center gap-[var(--space-inline)]">
        <span class="tabular-nums text-[color:var(--color-attention)]">{{
          moneyText(difference, true)
        }}</span>
        <span class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">{{
          kindLabel
        }}</span>
      </div>
      <div v-else class="flex items-center gap-[var(--space-inline)]">
        <span class="whitespace-nowrap text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('reconciliation.editor.notChecked') }}
        </span>
        <ActionButton
          v-if="!disabled"
          variant="secondary"
          class="whitespace-nowrap"
          :disabled="draft === null"
          @click="confirm"
        >
          {{ t('reconciliation.editor.confirm') }}
        </ActionButton>
      </div>
      <p v-if="changed" class="mt-1 text-[length:var(--text-step-1)] text-[color:var(--color-danger)]">
        {{ t('reconciliation.editor.balanceChanged') }}
      </p>
      <p v-if="errorDetail" class="mt-1 text-[length:var(--text-step-1)] text-[color:var(--color-danger)]">
        {{ errorDetail }}
      </p>
      <p
        v-if="saving"
        role="status"
        class="mt-1 text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
      >
        {{ t('reconciliation.editor.saving') }}
      </p>
    </td>
  </tr>
</template>
